const RN_WEB_HOST = 'mobile.a-bly.com';
const RN_WEB_SUFFIX = `.${RN_WEB_HOST}`;
const PAGE_WEB_URL = 'https://m.a-bly.com';
// 로컬 개발 시 웹뷰 콘텐츠(ably-webview-react) dev 서버 포트.
// 호스트 RN-Web dev 는 다른 포트라 접속 origin 이 아니라 이 포트를 써야 로컬 웹뷰에 붙는다.
const LOCAL_WEBVIEW_PORT = 3000;

/**
 * 웹 콘텐츠 base URL 을 호스트 앱(RN-Web)의 location.hostname 으로 결정한다.
 * (resolveWebURL.web 의 호스트 매핑과 동일 규칙)
 *
 * - mobile.a-bly.com (prod)          → https://m.a-bly.com
 * - stag-N.mobile.a-bly.com (stag)   → https://m-stag-N.a-bly.com
 * - RN-Web 호스트가 아니면(localhost / 페이지웹 등) → 접속 origin 그대로
 *   (로컬 개발 환경에서 prod(m.a-bly.com)로 새지 않고 현재 origin 을 쓰게 함)
 * - window 없음(SSR/native) → https://m.a-bly.com
 */
export const getWebBaseURLSync = (): string => {
  if (typeof window === 'undefined' || !window.location) return PAGE_WEB_URL;

  const { hostname, protocol } = window.location;

  if (hostname === RN_WEB_HOST) return PAGE_WEB_URL;

  if (hostname.endsWith(RN_WEB_SUFFIX)) {
    const stage = hostname.slice(0, -RN_WEB_SUFFIX.length); // 'stag-N'
    return `https://m-${stage}.a-bly.com`;
  }

  // localhost 등 RN-Web 호스트가 아닌 경우: 로컬 웹뷰 dev 서버(3000)로 연결
  return `${protocol}//${hostname}:${LOCAL_WEBVIEW_PORT}`;
};

/**
 * `getWebBaseURLSync` 의 async 래퍼(기존 호출부 호환).
 *
 * ⚠️ 새 탭(`window.open`)을 여는 경로에서는 sync 버전을 써야 한다. await 를 거치면
 * 브라우저의 user activation 이 끊겨 팝업 차단에 걸릴 수 있다.
 */
export const getWebBaseURL = async () => getWebBaseURLSync();

/**
 * **절대 URL 의 origin 만** 현재 접속 환경에 맞는 웹뷰 콘텐츠 origin 으로 교체한다.
 * path/query/hash 는 서버가 준 그대로 유지한다.
 *
 * - `mobile.a-bly.com` 에서 접속 → `m.a-bly.com`
 * - `stag-N.mobile.a-bly.com` 에서 접속 → `m-stag-N.a-bly.com`
 * - 그 외(localhost / LAN IP) → `{protocol}//{hostname}:3000` (로컬 웹뷰 dev 서버)
 *
 * 서버가 내려주는 웹뷰 URL 이 prod origin 으로 고정돼 있어도, 스테이징에서 접속하면
 * 스테이징 콘텐츠를 보게 하기 위한 보정이다. `new URL(url, base)` 로는 해결되지 않는다
 * — url 이 절대 URL 이면 base 가 무시되기 때문.
 *
 * ⚠️ web 전용. 네이티브는 `window` 가 없어 항상 prod 를 반환하므로 호출하면 안 된다.
 */
export const withWebviewOrigin = (url: string | URL): string => {
  const target = new URL(url.toString());
  const base = new URL(getWebBaseURLSync());

  target.protocol = base.protocol;
  target.host = base.host;

  return target.toString();
};
